import { useState, useEffect } from 'react'
import api from '../api/client'
import logger from '../utils/logger'

interface Entitlements {
  user_id: string
  plan_tier: string
  status: string
  is_active: boolean
  gpu_access: boolean
  limits: {
    jobs_per_day: number
    concurrent_jobs: number
    gpu_access: boolean
    features: string[]
  }
  current_period_end: string | null
  cancel_at_period_end: boolean
  is_affiliate?: boolean
}

export function useEntitlements() {
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchEntitlements() {
      if (!api.isAuthenticated()) {
        setEntitlements(null)
        setLoading(false)
        return
      }

      try {
        const data = await api.getEntitlements()
        setEntitlements(data)
        setError(null)
      } catch (err) {
        logger.error('Failed to fetch entitlements', err, { component: 'useEntitlements', action: 'fetchEntitlements' })
        setError(err instanceof Error ? err.message : 'Failed to load plan info')
      } finally {
        setLoading(false)
      }
    }

    fetchEntitlements()
  }, [])

  const hasFeature = (feature: string): boolean => {
    return entitlements?.limits.features.includes(feature) ?? false
  }

  const isFeatureGated = (feature: string): boolean => {
    return !hasFeature(feature)
  }

  return {
    entitlements,
    loading,
    error,
    hasFeature,
    isFeatureGated,
    planTier: entitlements?.plan_tier || 'free',
    isActive: entitlements?.is_active ?? false,
    isAffiliate: entitlements?.is_affiliate ?? false,
  }
}
